import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Image as ImageIcon, X, Loader } from 'lucide-react';
import { downscaleImage } from '../../utils/imageResize';
import { uploadFrameImage } from '../../services/frameStockService';
import CameraCaptureModal, { cameraAvailable } from './CameraCaptureModal';

// One photo for one frame colour. Picking a picture shrinks it on the device
// and uploads it straight away; the parent only ever holds the final URL.
// "Take photo" opens the in-app camera (webcam or the tablet's back camera);
// where the camera API is unavailable it falls back to the device's own
// picker, which on a tablet still offers the camera.
const FramePhotoPicker = ({ value, onChange, label }) => {
  const { t } = useTranslation();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [camOpen, setCamOpen] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFile(file);
  };

  const takePhoto = () => {
    if (cameraAvailable()) setCamOpen(true);
    else cameraRef.current?.click();
  };

  const handleFile = async (file) => {
    setError('');
    setBusy(true);
    try {
      const shrunk = await downscaleImage(file, { maxDim: 1000, quality: 0.82 });
      const url = await uploadFrameImage(shrunk);
      onChange(url);
    } catch (err) {
      setError(err.response?.status === 413 ? t('framePhoto.tooLarge') : (err.response?.data?.message || t('framePhoto.uploadFailed')));
    } finally {
      setBusy(false);
    }
  };

  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.8rem', borderRadius: 8,
    border: '1.5px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  };

  return (
    <div>
      {label && <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{label}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{
          width: 120, height: 80, borderRadius: 10, overflow: 'hidden', flex: 'none', position: 'relative',
          background: '#f8fafc', border: value ? '1px solid #e2e8f0' : '1.5px dashed #cbd5e1',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8',
        }}>
          {busy ? <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
            : value ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <Camera size={22} />}
          {value && !busy && (
            <button type="button" onClick={() => onChange('')} aria-label={t('framePhoto.remove')} title={t('framePhoto.remove')}
              style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.65)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <button type="button" style={btn} disabled={busy} onClick={takePhoto}>
            <Camera size={15} /> {t('framePhoto.takePhoto')}
          </button>
          <button type="button" style={btn} disabled={busy} onClick={() => galleryRef.current?.click()}>
            <ImageIcon size={15} /> {t('framePhoto.chooseFromGallery')}
          </button>
        </div>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFile} />
        <input ref={galleryRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
      </div>
      <CameraCaptureModal open={camOpen} onClose={() => setCamOpen(false)} onCapture={handleFile}
        onFallback={() => galleryRef.current?.click()} />
      {error
        ? <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.35rem' }}>{error}</div>
        : <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.35rem' }}>{t('framePhoto.optionalHint')}</div>}
    </div>
  );
};

export default FramePhotoPicker;
